import { Input, Skeleton, Textarea } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { NativeSelectField, NativeSelectRoot } from "@hydrologiq/shared/chakra/NativeSelect"

import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import {
  AssetStatusInfoInput,
  ChangeAssetStatusDocument,
  GetAssetHydrogenInformationDocument,
} from "@hydrologiq/gql/graphql"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
interface AssetLogStatusProps {
  asset: gqlTypes.Asset
  onUpdate?: (asset: gqlTypes.Asset) => void
  button?: boolean
}

import { useEffect, useState } from "react"
import { useLazyQuery, useMutation } from "@apollo/client"
import { useAppSelector } from "@hydrologiq/shared/state/hooks"
import AssetPopover from "@hydrologiq/shared/chakra/Popover"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import { useFetchSites, useSiteLoading } from "@hydrologiq/shared/providers/helpers/useSites"
import { getSites } from "@hydrologiq/shared/state/slices/sitesSlice"
import { IANATimeZone, toUserTZ } from "@hydrologiq/common/helpers/date"
import { toTimestamp } from "@hydrologiq/common/helpers/date"
import { nowDateTime } from "@hydrologiq/common/helpers/date"
import { hours } from "timezonecomplete"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "@hydrologiq/shared/copy/constants"
import { useCopyStatuses, useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import { toStatusItems } from "@hydrologiq/shared/asset/attributes/status"
const FuelLogInput = ({ assignments }: { assignments?: gqlTypes.AssetAssignmentsSummary }) => {
  const latestAssignment = assignments?.all?.find((a) => a.id === assignments?.latest)

  const [getHydrogenCurrent, { loading }] = useLazyQuery(GetAssetHydrogenInformationDocument)

  useEffect(() => {
    if (latestAssignment) {
      const now = nowDateTime()
      const end = latestAssignment.end ? toUserTZ(latestAssignment.end) : now
      const start = end.sub(hours(24))
      getHydrogenCurrent({
        variables: {
          installations: latestAssignment.installation?.id ?? "",
          assets: latestAssignment.asset ?? "",
          start: toTimestamp(start),
          stop: toTimestamp(end),
          location: IANATimeZone(),
        },
      }).then((res) => {
        res?.data?.weight?.value && setValue(Math.round(res.data.weight.value * 100) / 100)
      })
    }
  }, [latestAssignment])

  const [value, setValue] = useState(0)

  return loading ? (
    <Skeleton height="30px" width="100%" />
  ) : (
    <Field label="Amount of fuel (kg)">
      <Input
        name="fuel_level"
        placeholder="Enter amount of fuel"
        type="number"
        step={0.001}
        value={value}
        onChange={(e) => setValue(parseFloat(e.target.value))}
      />
    </Field>
  )
}

const AssetLogStatus = ({ asset, onUpdate, button = false }: AssetLogStatusProps) => {
  const [open, setOpen] = useState(false)
  const [changeStatus, { loading: changeStatusLoading }] = useMutation(ChangeAssetStatusDocument)

  const copyStatuses = useCopyStatuses(gqlTypes.TenantStatusCopyType.Asset)
  const statusItems = toStatusItems(copyStatuses)
  const [status, setStatus] = useState(statusItems.find((s) => s.value === asset.status)?.value)
  useEffect(() => {
    setStatus(statusItems.find((s) => s.value === asset.status)?.value)
  }, [asset.status])

  const sites = useAppSelector(getSites)
  const sitesLoading = useSiteLoading()
  const [site, setSite] = useState(asset.reserved_site?.id)

  useEffect(() => {
    setSite(asset.reserved_site?.id)
  }, [asset.reserved_site])

  const loadSites = useFetchSites()
  const sitesFeatureEnabled = useGetFeature("site_reservation")
  const servicingEnabled = useGetFeature("servicing")
  const showSiteDropdown = sitesFeatureEnabled

  // If servicing feature is disabled, don't render the component
  if (!servicingEnabled) {
    return null
  }

  const canReserveSite = status !== undefined && [gqlTypes.AssetStatus.Reserved].includes(status) && showSiteDropdown
  const canLogFuelLevel =
    status !== undefined &&
    [
      gqlTypes.AssetStatus.DispatchReady,
      gqlTypes.AssetStatus.Inbound,
      gqlTypes.AssetStatus.Deployed,
      gqlTypes.AssetStatus.DepotChecked,
    ].includes(status)

  useEffect(() => {
    if (open && canReserveSite) {
      loadSites()
    }
  }, [status, open])

  const loading = changeStatusLoading || sitesLoading

  const copy = useCopyValues({
    [COPY_KEYS.HEADINGS.SITE]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE],
    [COPY_KEYS.HEADINGS.ASSET]: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ASSET],
  })

  const siteItems = [...sites.map((site) => ({ label: site.name, value: site.id }))]

  useEffect(() => {
    if (site === undefined && siteItems.length > 0) {
      setSite(siteItems[0].value)
    }
  }, [siteItems, site])

  return (
    <AssetPopover
      iconType={button ? "changeStatus" : undefined}
      actionType={button ? "changeStatus" : undefined}
      placement="right-end"
      loading={loading}
      onOpenChange={setOpen}
      open={open}
      size="2xs"
    >
      <Form
        heading={`Change the ${copy[COPY_KEYS.HEADINGS.ASSET].toLocaleLowerCase()} status`}
        helperText={`Please provide information below to change the ${copy[
          COPY_KEYS.HEADINGS.ASSET
        ].toLocaleLowerCase()} status.`}
        actionType="change"
        loading={loading}
        onSubmit={async (data: FormData) => {
          const status = data.get("status")?.toString() as gqlTypes.AssetStatus
          const note = data.get("note")?.toString()

          let info: AssetStatusInfoInput = {}
          if (note) info.note = note
          if (site && canReserveSite && site !== "no_site") info.reserved_site = site
          else if (
            asset.reserved_site &&
            site !== "no_site" &&
            status !== gqlTypes.AssetStatus.Available &&
            showSiteDropdown
          )
            info.reserved_site = asset.reserved_site.id
          if (canLogFuelLevel) {
            const fuelLevel = data.get("fuel_level")?.toString()
            if (fuelLevel) info.fuel_level = parseFloat(fuelLevel)
          }
          if (status) {
            await changeStatus({ variables: { id: asset.id, status, info } })

            onUpdate &&
              onUpdate({
                ...asset,
                status,
                reserved_site:
                  site === undefined || status === gqlTypes.AssetStatus.Available
                    ? undefined
                    : sites.find((s) => s.id === info.reserved_site),
              })
            setOpen(false)
            return true
          }
          return false
        }}
        onCancel={() => setOpen(false)}
        canSubmit={
          !loading &&
          (status !== asset.status ||
            (canReserveSite &&
              site !== undefined &&
              site !== asset.reserved_site?.id &&
              !(site === "no_site" && asset.reserved_site?.id === undefined)))
        }
      >
        <>
          <Field label="Status" required>
            <NativeSelectRoot>
              <NativeSelectField
                onChange={(e) => setStatus(e.target.value as gqlTypes.AssetStatus)}
                name="status"
                defaultValue={asset.status ?? gqlTypes.AssetStatus.Available}
                value={status}
                items={statusItems}
              />
            </NativeSelectRoot>
          </Field>

          {canReserveSite && (
            <Field label={copy[COPY_KEYS.HEADINGS.SITE]} disabled={loading}>
              {sitesLoading ? (
                <Skeleton height="40px" width="100%" />
              ) : (
                <NativeSelectRoot>
                  <NativeSelectField
                    onChange={(e) => setSite(e.target.value)}
                    name="reserved_site"
                    key={sites?.length}
                    defaultValue={site || (siteItems.length > 0 ? siteItems[0].value : undefined)}
                    items={siteItems}
                  />
                </NativeSelectRoot>
              )}
            </Field>
          )}

          {canLogFuelLevel && <FuelLogInput assignments={asset.assignments ?? undefined} />}

          <Field label="Note">
            <Textarea name="note" placeholder="Enter a note here" />
          </Field>
        </>
      </Form>
    </AssetPopover>
  )
}

export default AssetLogStatus
